import { StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import {
  QueryClient,
  QueryClientProvider,
  HydrationBoundary,
} from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import "./global.scss";
// Shared page blocks (partners, dynamic-page sliders) must ship in the main CSS
// bundle so layout is correct on every route, including when route JS is split.
import "./components/Esg/Esg.scss";
import "./components/ProductPage/ProductPage.scss";
import App from "./App.tsx";
import i18n from "./i18n/config.ts";
import { MobileContext } from "./contexts/MobileContext";

// Detect mobile from User-Agent for SSR hydration match.
// useIsMobile keeps that value until the viewport changes (resize/orientation).
const isMobileUA = /iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent);

type WindowWithSSRState = typeof window & {
  __REACT_QUERY_STATE__?: unknown;
  __I18N_STATE__?: {
    locale: string;
    resources: Record<string, unknown>;
  };
};

// In production we don't want noisy console output.
// Keep console.warn/error for actionable diagnostics.
if (import.meta.env.PROD) {
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};

  // TEMP: Log hydration mismatch errors to diagnose the issue
  const prevOnError = window.onerror;
  window.onerror = (...args: unknown[]) => {
    const message = args[0];
    if (
      typeof message === "string" &&
      (message.includes("Minified React error #418") ||
        message.includes("hydrat"))
    ) {
      console.warn("[HYDRATION MISMATCH]", args);
      return true;
    }

    if (typeof prevOnError === "function") {
      return (prevOnError as (...innerArgs: unknown[]) => unknown)(...args) as
        | boolean
        | undefined;
    }

    return false;
  };
}

// Align i18n with server locale before hydration so first client render matches SSR (avoids #418)
const pathname = window.location.pathname;
const locale = pathname.startsWith("/ua")
  ? "uk"
  : pathname.startsWith("/en")
    ? "en"
    : pathname.startsWith("/pl")
      ? "pl"
      : pathname.startsWith("/ro")
        ? "ro"
        : pathname.startsWith("/hr")
          ? "hr"
          : pathname.startsWith("/el")
            ? "el"
            : pathname.startsWith("/bg")
              ? "bg"
              : pathname.startsWith("/lt")
                ? "lt"
                : pathname.startsWith("/it")
                  ? "it"
                  : pathname.startsWith("/hu")
                    ? "hu"
                    : "en";
i18n.language = locale;
if (i18n.options) i18n.options.lng = locale;
if (i18n.store) i18n.store.language = locale;

// Redirect from www to non-www
if (window.location.hostname.startsWith("www.")) {
  window.location.replace(
    window.location.protocol +
      "//" +
      window.location.hostname.substring(4) +
      window.location.pathname +
      window.location.search +
      window.location.hash,
  );
}

function applyInlineTranslations(targetLocale: string): void {
  const inlineState = (window as WindowWithSSRState).__I18N_STATE__;
  if (
    inlineState?.locale === targetLocale &&
    inlineState.resources &&
    Object.keys(inlineState.resources).length > 0
  ) {
    i18n.addResourceBundle(
      targetLocale,
      "translation",
      inlineState.resources,
      true,
      true,
    );
    return;
  }

  // Dev / fallback: fetch before hydrate only when SSR did not inline translations.
  void fetch(`/locales/${targetLocale}/translation.json`, {
    cache: "force-cache",
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((translations) => {
      if (!translations) return;
      i18n.addResourceBundle(
        targetLocale,
        "translation",
        translations,
        true,
        true,
      );
    })
    .catch((e) => {
      console.warn(
        `[i18n] Failed to preload translations for "${targetLocale}".`,
        e,
      );
    });
}

applyInlineTranslations(locale);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: false,
    },
  },
});

const dehydratedState = (window as WindowWithSSRState).__REACT_QUERY_STATE__;

hydrateRoot(
  document.getElementById("root")!,
  <StrictMode>
    <MobileContext.Provider value={isMobileUA}>
      <I18nextProvider i18n={i18n}>
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <HydrationBoundary state={dehydratedState}>
              <App />
            </HydrationBoundary>
          </QueryClientProvider>
        </BrowserRouter>
      </I18nextProvider>
    </MobileContext.Provider>
  </StrictMode>,
);

// Remove loading class after hydration is complete
requestAnimationFrame(() => {
  document.documentElement.classList.remove("loading");
});
